/**
 * Detect shift-factor labels that merely repeat what is already in the clinical presentation,
 * so they are not offered as "next questions" (e.g. "Fever on exam" when fever is in the HPI).
 *
 * @module ddxNextQuestionPresentationFilter
 */

/** Tokens that appear in many factor labels but are poor specificity for redundancy checks. */
const GENERIC_LABEL_TOKENS = new Set([
  'acute',
  'chronic',
  'clinical',
  'alternative',
  'present',
  'presenting',
  'recent',
  'severe',
  'mild',
  'patient',
  'history',
  'general',
  'other',
]);

function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * @param {string} presentationText
 * @param {string | null | undefined} factorLabel
 * @param {{ minTokenLength?: number } | undefined} [options]
 * @returns {boolean} true if this factor should not be suggested as a new question
 */
export function isShiftFactorRedundantWithPresentation(presentationText, factorLabel, options = {}) {
  const minTok =
    typeof options.minTokenLength === 'number' && options.minTokenLength >= 1
      ? options.minTokenLength
      : 5;
  const ground = String(presentationText || '')
    .toLowerCase()
    .normalize('NFKC');
  const raw = String(factorLabel || '').trim();
  if (!raw) return false;
  const L = raw.toLowerCase().normalize('NFKC');

  if (L.length >= 4 && ground.includes(L)) return true;

  const head = L.split(/\s*\(/)[0].trim();
  if (head.length >= 6 && head !== L && ground.includes(head)) return true;

  const tokens = L.split(/[^a-z0-9]+/i).filter(
    (t) => t.length >= minTok && !GENERIC_LABEL_TOKENS.has(t)
  );
  for (const t of tokens) {
    if (new RegExp(`\\b${escapeRegExp(t)}\\b`, 'i').test(ground)) return true;
  }
  return false;
}
